import React, { useEffect, useMemo, useState } from "react";
import { useDebounce } from "../hooks/useDebounce";

type Option = {
  id: number;
  label: string;
};

type AutocompleteInputProps = {
  label: string;
  placeholder?: string;
  value: number | null;
  selectedLabel?: string;
  disabled?: boolean;
  onSelect: (option: Option | null) => void;
  fetchOptions: (query: string) => Promise<Option[]>;
};

export const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  label,
  placeholder,
  value,
  selectedLabel,
  disabled,
  onSelect,
  fetchOptions,
}) => {
  const [query, setQuery] = useState(selectedLabel ?? "");
  const [options, setOptions] = useState<Option[]>([]);
  const [open, setOpen] = useState(false);
  const debounced = useDebounce(query, 350);

  useEffect(() => {
    setQuery(selectedLabel ?? "");
  }, [selectedLabel, value]);

  useEffect(() => {
    const load = async () => {
      if (disabled) {
        setOptions([]);
        return;
      }
      if (debounced.trim().length < 2) {
        setOptions([]);
        return;
      }
      const results = await fetchOptions(debounced.trim());
      setOptions(results);
    };
    load();
  }, [debounced, fetchOptions, disabled]);

  const showList = useMemo(
    () => open && options.length > 0,
    [open, options.length]
  );

  return (
    <div className="mb-3 position-relative">
      <label className="form-label">{label}</label>
      <input
        className="form-control"
        placeholder={placeholder}
        value={query}
        disabled={disabled}
        onFocus={() => {
          if (!disabled) {
            setOpen(true);
          }
        }}
        onBlur={() => {
          if (!disabled) {
            setTimeout(() => setOpen(false), 150);
          }
        }}
        onChange={(event) => {
          if (disabled) {
            return;
          }
          setQuery(event.target.value);
          onSelect(null);
        }}
      />
      {showList && (
        <div className="list-group position-absolute w-100 shadow-sm">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              className="list-group-item list-group-item-action"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onSelect(option);
                setQuery(option.label);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
